import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { useEffect, useRef, useState } from "react";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { useSavePayrollStatementNote } from "./query";

const SAVE_STATUS = {
  error: "error",
  idle: "idle",
  saved: "saved",
} as const;
type SaveStatus = (typeof SAVE_STATUS)[keyof typeof SAVE_STATUS];

const NOTE_SAVE_DELAY_MS = 700;

export const NoteComposer = ({
  businessId,
  periodStart,
  technicianId,
  value,
  onChange,
  placeholder,
  disabled = false,
}: {
  businessId: string | null;
  periodStart: string | null;
  technicianId: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
}) => {
  const { mutate: saveNote } = useSavePayrollStatementNote(
    businessId,
    periodStart,
  );
  const [status, setStatus] = useState<SaveStatus>(SAVE_STATUS.idle);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const technicianIdRef = useRef(technicianId);
  const valueRef = useRef(value);
  const disabledRef = useRef(disabled);

  disabledRef.current = disabled;

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (!disabled || !timerRef.current) return;

    clearTimeout(timerRef.current);
    timerRef.current = null;
    setStatus(SAVE_STATUS.idle);
  }, [disabled]);

  useEffect(() => {
    technicianIdRef.current = technicianId;
    setStatus(SAVE_STATUS.idle);
    return () => {
      if (!timerRef.current) return;

      clearTimeout(timerRef.current);
      timerRef.current = null;
      if (!disabledRef.current) {
        saveNote({ technicianId, note: valueRef.current });
      }
    };
  }, [saveNote, technicianId]);

  const handleChange = (next: string) => {
    if (disabledRef.current) return;

    onChange(next);
    setStatus(SAVE_STATUS.idle);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (disabledRef.current) {
        timerRef.current = null;
        return;
      }

      const requestTechnicianId = technicianId;
      saveNote(
        { technicianId, note: next },
        {
          onError: () => {
            if (technicianIdRef.current === requestTechnicianId) {
              setStatus(SAVE_STATUS.error);
            }
          },
          onSuccess: () => {
            if (technicianIdRef.current === requestTechnicianId) {
              setStatus(SAVE_STATUS.saved);
            }
          },
        },
      );
      timerRef.current = null;
    }, NOTE_SAVE_DELAY_MS);
  };

  return (
    <div className="relative w-full">
      <Textarea
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        aria-label="Note"
        disabled={disabled}
        rows={6}
        className="min-h-[150px] w-full resize-y"
      />
      {status === SAVE_STATUS.saved && (
        <Stack
          direction="row"
          align="center"
          gap={1}
          className="absolute top-2 right-2 rounded bg-surface-overlay/90 px-1.5 py-0.5"
        >
          <CheckIcon className="h-3.5 w-3.5 text-success" />
          <Text variant="caption" tone="muted">
            Saved
          </Text>
        </Stack>
      )}
      {status === SAVE_STATUS.error && (
        <Text
          variant="caption"
          tone="destructive"
          className="absolute top-2 right-2 rounded bg-surface-overlay/90 px-1.5 py-0.5"
        >
          Couldn't save
        </Text>
      )}
    </div>
  );
};
